import { Col, Form, FormInstance, ModalProps } from 'antd';
import moment, { MomentInput } from 'moment';
import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Plans } from 'api/billing/types';
import { Button } from 'components/atoms/button';
import { Input, TextArea } from 'components/atoms/input';
import { Select } from 'components/atoms/select';
import { useTranslation } from 'hooks/use-translation';
import { usePlanName } from 'queries/billing';
import {
  Card,
  CardWrapper,
  ConfirmButton,
  ModalContent,
  StyledModal,
  TitleWrapper,
} from './styled-components';

type CancellationForm = {
  cancelReason: string; // enum to be used
  cancelReasonDescription: string;
  vendorToolName: string;
  missingItemsFeedback: string;
};

const ContentHeading = styled.div`
  font-size: var(--heading-6-size);
  padding: var(--layout-04) 0;
`;

const StyledForm = styled(Form)`
  width: 50%;
  min-width: 600px;

  .ant-form-item-label > label {
    height: 100%;
  }

  .ant-form-item {
    margin-bottom: var(--spacing-06);
  }
`;

const cancelReasonOptions = [
  'Product does not meet our requirements or business needs',
  'Customer service is not satisfactory',
  'We no longer have a need for this',
  'Moving to another tool',
  'Price is not a fit for us',
  'Other',
];

const ThingsToRememberCard = ({
  index,
  currentPlan,
  subscriptionEndDate,
}: {
  index: number;
  currentPlan: Plans;
  subscriptionEndDate: MomentInput;
}) => {
  const currentPlanName = usePlanName(currentPlan);

  return (
    <Col span={8}>
      <Card className="things-to-remember-card">
        <div className="card-title">{index}</div>
        <div className="card-content">
          <FormattedMessage
            id={`billing.cancel.things.to.remember.card.content.${index}`}
            values={{
              currentPlan: currentPlanName,
              subscriptionEndDate:
                moment(subscriptionEndDate).format('DD MMM YYYY'),
              br: <br />,
            }}
          />
        </div>
      </Card>
    </Col>
  );
};

const Title = () => {
  return (
    <TitleWrapper>
      <FormattedMessage id="billing.cancel.confirmation.title" />
    </TitleWrapper>
  );
};

const StepOneFooter = ({
  onConfirm,
  onCancel,
}: {
  onConfirm: () => void;
  onCancel: ModalProps['onCancel'];
}) => {
  return (
    <>
      <Button type="text" onClick={onCancel}>
        <FormattedMessage id="go.back" />
      </Button>
      <ConfirmButton type="primary" danger onClick={onConfirm}>
        <FormattedMessage id="next" />
      </ConfirmButton>
    </>
  );
};

const StepTwoFooter = ({
  isScheduling,
  onOk,
  onCancel,
}: {
  isScheduling: boolean;
  onOk: ModalProps['onOk'];
  onCancel: () => void;
}) => {
  return (
    <>
      <Button type="text" onClick={onCancel}>
        <FormattedMessage id="go.back" />
      </Button>
      <ConfirmButton
        type="primary"
        danger
        onClick={onOk}
        loading={isScheduling}
      >
        <FormattedMessage id="billing.cancel.confirmation.button" />
      </ConfirmButton>
    </>
  );
};

const StepOneContent = ({ form }: { form: FormInstance<CancellationForm> }) => {
  const descriptionPlaceholder = useTranslation(
    'billing.cancel.reason.description.input.placeholder',
  );
  const missingItemsPlaceholder = useTranslation(
    'billing.cancel.reason.four.description.input.placeholder',
  );

  return (
    <>
      <ContentHeading>
        <FormattedMessage id="billing.cancel.confirmation.content.heading.step.one" />
      </ContentHeading>
      <StyledForm
        form={form}
        autoComplete="off"
        layout="vertical"
        colon={false}
      >
        <Form.Item
          required
          name="cancelReason"
          label={<FormattedMessage id="billing.cancel.reason.label" />}
        >
          <Select
            defaultMenuIsOpen={!form.getFieldValue('cancelReason')}
            options={cancelReasonOptions.map((option, index) => ({
              label: (
                <FormattedMessage id={`billing.cancel.reason.${index + 1}`} />
              ),
              value: option,
            }))}
          />
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.cancelReason?.value !== currentValues.cancelReason?.value
          }
        >
          {({ getFieldValue }) =>
            getFieldValue('cancelReason')?.value &&
            getFieldValue('cancelReason')?.value !== cancelReasonOptions[3] ? (
              <Form.Item name="cancelReasonDescription">
                <TextArea placeholder={descriptionPlaceholder} autoFocus />
              </Form.Item>
            ) : null
          }
        </Form.Item>
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.cancelReason?.value !== currentValues.cancelReason?.value
          }
        >
          {({ getFieldValue }) =>
            getFieldValue('cancelReason')?.value === cancelReasonOptions[3] ? (
              <>
                <Form.Item
                  name="vendorToolName"
                  label={
                    <FormattedMessage id="billing.cancel.reason.four.select.label" />
                  }
                >
                  <Input autoFocus />
                </Form.Item>
                <Form.Item name="missingItemsFeedback">
                  <TextArea placeholder={missingItemsPlaceholder} />
                </Form.Item>
              </>
            ) : null
          }
        </Form.Item>
      </StyledForm>
    </>
  );
};

const StepTwoContent = ({
  currentPlan,
  subscriptionEndDate,
}: {
  currentPlan: Plans;
  subscriptionEndDate: MomentInput;
}) => {
  return (
    <>
      <FormattedMessage id="billing.cancel.confirmation.content.heading.step.two" />
      <CardWrapper gutter={[40, 40]} style={{ marginTop: 'var(--spacing-08)' }}>
        {[1, 2, 3].map((value) => (
          <ThingsToRememberCard
            subscriptionEndDate={subscriptionEndDate}
            currentPlan={currentPlan}
            index={value}
          />
        ))}
      </CardWrapper>
    </>
  );
};

export const CancelConfirmation = ({
  currentPlan,
  subscriptionEndDate,
  isScheduling,
  onSubmit,
  onCancel,
}: ModalProps & {
  currentPlan: Plans;
  subscriptionEndDate: MomentInput;
  isScheduling: boolean;
  onSubmit: (formValues) => void;
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formValues, setFormValues] = useState<CancellationForm | null>(null);
  const [form] = Form.useForm();

  const onConfirm = useCallback(async () => {
    const _formValues = await form.validateFields();

    setFormValues({
      ..._formValues,
      cancelReason: _formValues.cancelReason.value,
    });
    setCurrentStep(2);
  }, [form]);

  const _onSubmit = useCallback(() => {
    if (formValues) {
      onSubmit(formValues);
    }
  }, [formValues, onSubmit]);

  return (
    <StyledModal
      width={1360}
      visible
      onCancel={onCancel}
      title={<Title />}
      footer={
        currentStep === 1 ? (
          <StepOneFooter onConfirm={onConfirm} onCancel={onCancel} />
        ) : (
          <StepTwoFooter
            isScheduling={isScheduling}
            onOk={_onSubmit}
            onCancel={() => setCurrentStep(1)}
          />
        )
      }
    >
      <ModalContent>
        {currentStep === 1 ? (
          <StepOneContent form={form} />
        ) : (
          <StepTwoContent
            currentPlan={currentPlan}
            subscriptionEndDate={subscriptionEndDate}
          />
        )}
      </ModalContent>
    </StyledModal>
  );
};
